// Facturación electrónica DIAN real (agosto 2026) — configuración de la
// empresa (NIT/resolución/prefijo/clave técnica), ambiente (habilitación o
// producción) y certificado digital. Ver app/dian_ubl_utils.py y la sección
// "Facturación electrónica DIAN real" del README para el detalle del motor
// que usa esta configuración (CUFE, XML UBL, firma XAdES-EPES, envío SOAP).
// El botón que de verdad timbra una factura vive en Comercial → Facturas
// (columna "Estado DIAN") — esta pantalla solo prepara la configuración.
import React, { useEffect, useState } from "react";
import { Shell } from "../components/Shell";
import { api, ApiError } from "../api";
import { useAuth } from "../auth/AuthContext";

type ConfigDian = {
  configurado: boolean;
  certificadoCargado: boolean;
  llaveCargada: boolean;
  activo: boolean;
  actualizadoEn?: string | null;
  nitEmisor: string | null;
  dvEmisor: string | null;
  razonSocialEmisor: string | null;
  direccionEmisor: string | null;
  ciudadEmisor: string | null;
  departamentoEmisor: string | null;
  prefijo: string | null;
  resolucionNumero: string | null;
  resolucionFecha: string | null;
  rangoDesde: number | null;
  rangoHasta: number | null;
  rangoVigenciaHasta: string | null;
  claveTecnica: string | null;
  softwareId: string | null;
  pinSeguridad: string | null;
  ambiente: string;
  politicaFirmaUrl: string | null;
  politicaFirmaHash: string | null;
};

const VACIO: ConfigDian = {
  configurado: false, certificadoCargado: false, llaveCargada: false, activo: false,
  nitEmisor: "", dvEmisor: "", razonSocialEmisor: "", direccionEmisor: "", ciudadEmisor: "", departamentoEmisor: "",
  prefijo: "", resolucionNumero: "", resolucionFecha: "", rangoDesde: null, rangoHasta: null, rangoVigenciaHasta: "",
  claveTecnica: "", softwareId: "", pinSeguridad: "", ambiente: "habilitacion", politicaFirmaUrl: "", politicaFirmaHash: "",
};

export function Dian() {
  const { tienePermiso } = useAuth();
  const puedeConfigurar = tienePermiso("dian:configurar");

  const [config, setConfig] = useState<ConfigDian>(VACIO);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [mensaje, setMensaje] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [archivoCert, setArchivoCert] = useState<File | null>(null);
  const [archivoLlave, setArchivoLlave] = useState<File | null>(null);
  const [passwordLlave, setPasswordLlave] = useState("");
  const [subiendo, setSubiendo] = useState(false);

  const cargar = async () => {
    setCargando(true);
    setError(null);
    try {
      const data = await api.get("/dian/configuracion");
      setConfig({ ...VACIO, ...data });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo cargar la configuración DIAN.");
    } finally {
      setCargando(false);
    }
  };
  useEffect(() => { cargar(); }, []);

  const campo = (clave: keyof ConfigDian) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setConfig((c) => ({ ...c, [clave]: e.target.value }));
  };

  const guardar = async (e: React.FormEvent) => {
    e.preventDefault();
    setGuardando(true);
    setMensaje(null);
    setError(null);
    try {
      const data = await api.put("/dian/configuracion", {
        nitEmisor: config.nitEmisor, dvEmisor: config.dvEmisor, razonSocialEmisor: config.razonSocialEmisor,
        direccionEmisor: config.direccionEmisor, ciudadEmisor: config.ciudadEmisor, departamentoEmisor: config.departamentoEmisor,
        prefijo: config.prefijo, resolucionNumero: config.resolucionNumero, resolucionFecha: config.resolucionFecha,
        rangoDesde: config.rangoDesde, rangoHasta: config.rangoHasta, rangoVigenciaHasta: config.rangoVigenciaHasta,
        claveTecnica: config.claveTecnica, softwareId: config.softwareId, pinSeguridad: config.pinSeguridad,
        ambiente: config.ambiente, politicaFirmaUrl: config.politicaFirmaUrl, politicaFirmaHash: config.politicaFirmaHash,
        activo: config.activo,
      });
      setConfig({ ...VACIO, ...data });
      setMensaje("Configuración guardada.");
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo guardar la configuración.");
    } finally {
      setGuardando(false);
    }
  };

  const subirCertificado = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!archivoCert || !archivoLlave) { setError("Seleccione el certificado (.pem) y la llave privada (.pem)."); return; }
    setSubiendo(true);
    setMensaje(null);
    setError(null);
    try {
      const form = new FormData();
      form.append("certificado", archivoCert);
      form.append("llave", archivoLlave);
      if (passwordLlave) form.append("password", passwordLlave);
      const data = await api.postForm("/dian/certificado", form);
      setConfig({ ...VACIO, ...data });
      setArchivoCert(null);
      setArchivoLlave(null);
      setPasswordLlave("");
      setMensaje("Certificado y llave privada cargados correctamente en el servidor.");
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo cargar el certificado.");
    } finally {
      setSubiendo(false);
    }
  };

  const borrarCertificado = async () => {
    if (!window.confirm("¿Borrar el certificado y la llave privada cargados? La empresa no podrá timbrar hasta cargar otros.")) return;
    setError(null);
    try {
      const data = await api.del("/dian/certificado");
      setConfig({ ...VACIO, ...data });
      setMensaje("Certificado eliminado del servidor.");
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo borrar el certificado.");
    }
  };

  return (
    <Shell titulo="Facturación DIAN" breadcrumb="Facturación electrónica DIAN">
      <p className="text-sm text-textmuted mb-4">
        Datos de la empresa ante la DIAN, el certificado digital y el ambiente (habilitación de pruebas o producción)
        que usa el motor de timbrado real (ver <code className="bg-slate-50 px-1 rounded">app/dian_ubl_utils.py</code>).
        El botón para timbrar cada factura está en Comercial → Facturas. Complete primero la habilitación ante la DIAN
        por fuera de SIGEPRO (RUT actualizado, certificado ONAC, asistente de habilitación del portal de la DIAN) antes
        de marcar esta configuración como <b>activa</b>.
      </p>

      {mensaje && <div className="rounded-md bg-accent-light border border-accent/40 px-3 py-2 text-sm text-accent-dark mb-4">{mensaje}</div>}
      {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700 mb-4">{error}</div>}

      {cargando ? (
        <p className="text-sm text-textmuted">Cargando...</p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <form onSubmit={guardar} className="card p-5 space-y-3">
            <h3 className="text-sm font-semibold text-slate-800 mb-1">Datos del emisor y resolución de facturación</h3>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">NIT emisor *</label>
                <input className="input-field" value={config.nitEmisor || ""} onChange={campo("nitEmisor")} disabled={!puedeConfigurar} required />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">DV</label>
                <input className="input-field" value={config.dvEmisor || ""} onChange={campo("dvEmisor")} disabled={!puedeConfigurar} />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Razón social *</label>
              <input className="input-field" value={config.razonSocialEmisor || ""} onChange={campo("razonSocialEmisor")} disabled={!puedeConfigurar} required />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Dirección</label>
              <input className="input-field" value={config.direccionEmisor || ""} onChange={campo("direccionEmisor")} disabled={!puedeConfigurar} />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Ciudad</label>
                <input className="input-field" value={config.ciudadEmisor || ""} onChange={campo("ciudadEmisor")} disabled={!puedeConfigurar} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Departamento</label>
                <input className="input-field" value={config.departamentoEmisor || ""} onChange={campo("departamentoEmisor")} disabled={!puedeConfigurar} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Prefijo de facturación *</label>
                <input className="input-field" value={config.prefijo || ""} onChange={campo("prefijo")} disabled={!puedeConfigurar} required />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Ambiente</label>
                <select className="input-field" value={config.ambiente} onChange={campo("ambiente")} disabled={!puedeConfigurar}>
                  <option value="habilitacion">Habilitación (pruebas)</option>
                  <option value="produccion">Producción</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">N.º resolución DIAN</label>
                <input className="input-field" value={config.resolucionNumero || ""} onChange={campo("resolucionNumero")} disabled={!puedeConfigurar} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Fecha resolución</label>
                <input className="input-field" type="date" value={config.resolucionFecha || ""} onChange={campo("resolucionFecha")} disabled={!puedeConfigurar} />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Rango desde</label>
                <input className="input-field" type="number" value={config.rangoDesde ?? ""} onChange={(e) => setConfig((c) => ({ ...c, rangoDesde: e.target.value ? Number(e.target.value) : null }))} disabled={!puedeConfigurar} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Rango hasta</label>
                <input className="input-field" type="number" value={config.rangoHasta ?? ""} onChange={(e) => setConfig((c) => ({ ...c, rangoHasta: e.target.value ? Number(e.target.value) : null }))} disabled={!puedeConfigurar} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Vigencia hasta</label>
                <input className="input-field" type="date" value={config.rangoVigenciaHasta || ""} onChange={campo("rangoVigenciaHasta")} disabled={!puedeConfigurar} />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Clave técnica (habilitación)</label>
              <input className="input-field" value={config.claveTecnica || ""} onChange={campo("claveTecnica")} disabled={!puedeConfigurar} />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">ID de software</label>
                <input className="input-field" value={config.softwareId || ""} onChange={campo("softwareId")} disabled={!puedeConfigurar} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">PIN de seguridad</label>
                <input className="input-field" value={config.pinSeguridad || ""} onChange={campo("pinSeguridad")} disabled={!puedeConfigurar} />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">URL política de firma (XAdES)</label>
              <input className="input-field" value={config.politicaFirmaUrl || ""} onChange={campo("politicaFirmaUrl")} disabled={!puedeConfigurar} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Hash SHA-256 de la política de firma</label>
              <input className="input-field" value={config.politicaFirmaHash || ""} onChange={campo("politicaFirmaHash")} disabled={!puedeConfigurar} />
              <p className="text-[11px] text-textmuted mt-1">
                Confirme la URL y el hash vigentes en el Anexo Técnico de Factura Electrónica de la DIAN antes de activar producción — SIGEPRO no los asume por usted.
              </p>
            </div>

            <label className="flex items-center gap-2 text-xs font-semibold text-slate-600 pt-1">
              <input type="checkbox" checked={config.activo} onChange={(e) => setConfig((c) => ({ ...c, activo: e.target.checked }))} disabled={!puedeConfigurar} />
              Configuración activa (permite timbrar facturas de verdad)
            </label>

            {puedeConfigurar ? (
              <button type="submit" className="btn-primary" disabled={guardando}>{guardando ? "Guardando..." : "Guardar configuración"}</button>
            ) : (
              <p className="text-xs text-textmuted">No tiene permiso para editar (dian:configurar).</p>
            )}
          </form>

          <div className="space-y-6">
            <div className="card p-5">
              <h3 className="text-sm font-semibold text-slate-800 mb-2">Certificado digital</h3>
              <p className="text-xs text-textmuted mb-3">
                El certificado (.pem/.cer) y la llave privada (.pem/.key) se guardan SOLO en el disco del servidor,
                fuera de cualquier carpeta servida al navegador — la base de datos nunca guarda el contenido de la
                llave privada, solo su ruta interna. Si la llave está cifrada, indique la contraseña una sola vez:
                se usa para descifrarla al cargarla y no se guarda en ningún lado.
              </p>
              <div className="flex items-center gap-4 mb-3 text-xs">
                <span className={`badge ${config.certificadoCargado ? "bg-accent-light text-accent-dark" : "bg-slate-100 text-slate-500"}`}>
                  Certificado {config.certificadoCargado ? "cargado" : "sin cargar"}
                </span>
                <span className={`badge ${config.llaveCargada ? "bg-accent-light text-accent-dark" : "bg-slate-100 text-slate-500"}`}>
                  Llave privada {config.llaveCargada ? "cargada" : "sin cargar"}
                </span>
              </div>
              {puedeConfigurar ? (
                <form onSubmit={subirCertificado} className="space-y-3">
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Certificado (.pem/.cer)</label>
                    <input className="input-field" type="file" accept=".pem,.cer,.crt" onChange={(e) => setArchivoCert(e.target.files?.[0] || null)} />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Llave privada (.pem/.key)</label>
                    <input className="input-field" type="file" accept=".pem,.key" onChange={(e) => setArchivoLlave(e.target.files?.[0] || null)} />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Contraseña de la llave (si está cifrada)</label>
                    <input className="input-field" type="password" value={passwordLlave} onChange={(e) => setPasswordLlave(e.target.value)} />
                  </div>
                  <div className="flex gap-3">
                    <button type="submit" className="btn-primary" disabled={subiendo || !archivoCert || !archivoLlave}>
                      {subiendo ? "Cargando..." : "Cargar certificado"}
                    </button>
                    {config.certificadoCargado && (
                      <button type="button" className="btn-secondary text-red-700" onClick={borrarCertificado}>Borrar certificado</button>
                    )}
                  </div>
                </form>
              ) : (
                <p className="text-xs text-textmuted">No tiene permiso para cargar el certificado (dian:configurar).</p>
              )}
            </div>

            <div className="card p-5 bg-amber-50 border-amber-200">
              <h3 className="text-sm font-semibold text-amber-800 mb-2">Límites honestos de esta integración</h3>
              <p className="text-xs text-amber-800 leading-relaxed">
                Este módulo construye y firma criptográficamente la factura (CUFE, XML UBL 2.1, firma XAdES-EPES, sobre
                SOAP con WS-Security) siguiendo la guía de integración entregada, y esa mecánica se verificó de forma
                independiente. Pero nunca se probó contra el Web Service real de la DIAN: eso requiere un certificado
                digital emitido por una entidad ONAC y haber completado la habilitación de la empresa ante la DIAN
                (trámite externo a SIGEPRO). Hasta que eso esté listo, use el ambiente "Habilitación (pruebas)" y
                revise la respuesta de la DIAN en cada intento de timbrado.
              </p>
            </div>
          </div>
        </div>
      )}
    </Shell>
  );
}
